import { Box, Button, Card, CardContent, Chip, Link as MuiLink, Typography } from "@mui/material";
import type { DocAttachment } from "@xop/dashboard-shared";
import type { JSX } from "react";

import { PdfInlinePreview } from "../PdfInlinePreview.js";
import { formatBytes } from "../utils.js";

/** True for an attachment that should preview inline as a PDF — by MIME
 * type, falling back to the `.pdf` extension when the upstream omitted the
 * content type. */
function isPdfAttachment(a: DocAttachment): boolean {
  return (
    a.content_type === "application/pdf" ||
    a.filename.toLowerCase().endsWith(".pdf")
  );
}

/** Right-hand panel for upload-mode docs in edit view. Pulled out to a
 * helper so the parent's `attachments[0]` indexing collapses into a
 * single `attachment | undefined` prop, which TypeScript's
 * `noUncheckedIndexedAccess` is happy with.
 *
 * When the attached file is a PDF and we know the owning `docId`, the panel
 * renders the file inline via `<PdfInlinePreview>` (pdf.js → `<canvas>`, fed
 * by the same-origin `/file` passthrough) so the editor SEES the document it's
 * editing instead of an "Open current file ↗" link. Same component the reader
 * uses — never a cross-origin `<iframe>` (the macOS-desktop hang). */
export function AttachmentPanel({
  attachment,
  docId,
  onPick,
  onRemove,
  busy,
}: {
  attachment: DocAttachment | undefined;
  /** Owning document id — required to stream the PDF bytes for the inline
   * preview. Omitted (e.g. a not-yet-saved doc) ⇒ no inline preview, the
   * "Open current file" link still shows. */
  docId?: number;
  onPick: () => void;
  onRemove: (id: number) => void;
  busy: boolean;
}): JSX.Element {
  if (!attachment) {
    return (
      <Card>
        <CardContent sx={{ py: 5, textAlign: "center" }}>
          <Typography sx={{ mb: 1.5, fontSize: 14, color: "text.disabled" }}>
            No file attached.
          </Typography>
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            size="small"
            onClick={onPick}
            disabled={busy}
            sx={{ bgcolor: "action.hover" }}
          >
            {busy ? "Uploading…" : "Pick a file"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ display: "grid", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14, fontWeight: 500 }}>{attachment.filename}</Box>
            <Box sx={{ fontSize: "11px", color: "text.disabled" }}>
              {attachment.content_type ?? "unknown"} · {formatBytes(attachment.byte_size)}
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button type="button" variant="text" color="inherit" size="small" onClick={onPick} disabled={busy}>
              Replace
            </Button>
            <Button type="button" variant="text" color="inherit" size="small" onClick={() => onRemove(attachment.id)}>
              <Box component="span" sx={{ color: "error.main" }}>Remove</Box>
            </Button>
          </Box>
        </Box>
        {(attachment.preview_url ?? attachment.download_url) && (
          <MuiLink
            href={attachment.preview_url ?? attachment.download_url ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ fontSize: 12, color: "primary.main" }}
          >
            Open current file ↗
          </MuiLink>
        )}
        <Box>
          <Chip label="DO bucket: xop-storage" size="small" sx={{ height: 22, borderRadius: 0.75, fontSize: 12, bgcolor: "surfaceSoft", color: "text.secondary" }} />
        </Box>
        {docId !== undefined && isPdfAttachment(attachment) && (
          <PdfInlinePreview docId={docId} filename={attachment.filename} />
        )}
      </CardContent>
    </Card>
  );
}
